import React from "react";

import {
  Box,
  FormControl,
  Select,
  SelectChangeEvent,
  MenuItem,
  Typography,
  Chip,
} from "@mui/material";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";

export interface AspectRatio {
  id: string;
  name: string;
  ratio: string;
  width: number;
  height: number;
  description: string;
  platform: string;
}

interface AspectRatioSelectorProps {
  selectedRatio: AspectRatio;
  onRatioChange: (ratio: AspectRatio) => void;
}

const ASPECT_RATIOS: AspectRatio[] = [
  {
    id: "standard",
    name: "Standard",
    ratio: "16:9",
    width: 1920,
    height: 1080,
    description: "Standard landscape format for YouTube and desktop",
    platform: "YouTube, Desktop",
  },
  {
    id: "youtube-shorts",
    name: "YouTube Shorts",
    ratio: "9:16",
    width: 1080,
    height: 1920,
    description: "Optimized for YouTube Shorts",
    platform: "YouTube Shorts",
  },
  {
    id: "instagram",
    name: "Instagram",
    ratio: "4:5",
    width: 1080,
    height: 1350,
    description: "Optimized for Instagram feed posts",
    platform: "Instagram",
  },
  {
    id: "tiktok",
    name: "TikTok",
    ratio: "9:16",
    width: 1080,
    height: 1920,
    description: "Optimized for TikTok format",
    platform: "TikTok",
  },
];

export default function AspectRatioSelector({
  selectedRatio,
  onRatioChange,
}: AspectRatioSelectorProps) {
  const handleRatioChange = (event: SelectChangeEvent<string>) => {
    const ratioId = event.target.value;
    const ratio = ASPECT_RATIOS.find((r) => r.id === ratioId);
    if (ratio) {
      onRatioChange(ratio);
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          background: "#fff",
          borderRadius: 2,
          boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
          p: 3,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AspectRatioIcon sx={{ color: "#6366f1", fontSize: 20 }} />
            <Typography
              component="h3"
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: "1.1rem",
                color: "#1f2937",
              }}
            >
              Video Format
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            borderBottom: "1.5px solid #f0f1f3",
            mb: 2,
          }}
        />

        <FormControl fullWidth>
          <Select
            value={selectedRatio?.id || ""}
            onChange={handleRatioChange}
            size="small"
            sx={{
              borderRadius: 2,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e5e7eb",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6366f1",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6366f1",
              },
              "& .MuiSelect-select": {
                paddingRight: "40px !important", // More space for arrow
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  minWidth: "480px !important", // Much wider to prevent overlap
                  maxWidth: "none",
                  "& .MuiMenuItem-root": {
                    padding: "16px 24px", // More generous padding
                    minHeight: "auto",
                    whiteSpace: "normal",
                    width: "100%",
                    overflow: "hidden", // Prevent overflow
                  },
                },
              },
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              disableScrollLock: true, // Prevent scroll issues
            }}
          >
            {ASPECT_RATIOS.map((ratio) => (
              <MenuItem
                key={ratio.id}
                value={ratio.id}
                sx={{
                  whiteSpace: "normal",
                  display: "block", // Ensure block layout
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    py: 0.5,
                    width: "100%",
                    maxWidth: "100%", // Ensure it doesn't exceed container
                    overflow: "hidden", // Prevent overflow
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                      maxWidth: "100%", // Constrain width
                    }}
                  >
                    <Typography
                      component="div"
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {ratio.name}
                    </Typography>
                    <Chip
                      label={ratio.ratio}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.75rem",
                        backgroundColor: "#f3f4f6",
                        color: "#6b7280",
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                  <Typography
                    component="div"
                    variant="body2"
                    sx={{
                      color: "#6b7280",
                      fontSize: "0.8rem",
                      lineHeight: 1.3,
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      maxWidth: "100%",
                      overflow: "hidden",
                    }}
                  >
                    {ratio.description} • {ratio.platform}
                  </Typography>
                  <Typography
                    component="div"
                    variant="body2"
                    sx={{
                      color: "#9ca3af",
                      fontSize: "0.75rem",
                      lineHeight: 1.3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {ratio.width} × {ratio.height}px
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          sx={{
            backgroundColor: "#f8fafc",
            borderRadius: 2,
            p: 2,
            border: "1px solid #e2e8f0",
          }}
        >
          <Typography
            component="div"
            variant="body2"
            sx={{ fontWeight: 600, color: "#374151", mb: 1 }}
          >
            Current Selection:
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography
              component="div"
              variant="body1"
              sx={{ fontWeight: 600 }}
            >
              {selectedRatio?.name || "No selection"}
            </Typography>
            {selectedRatio && (
              <Chip
                label={selectedRatio.ratio}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.75rem",
                  backgroundColor: "#6366f1",
                  color: "white",
                }}
              />
            )}
          </Box>
          <Typography
            component="div"
            variant="body2"
            sx={{ color: "#6b7280", fontSize: "0.8rem", mb: 0.5 }}
          >
            {selectedRatio?.description || "Please select a format"}
          </Typography>
          <Typography
            component="div"
            variant="body2"
            sx={{ color: "#6b7280", fontSize: "0.8rem", mb: 0.5 }}
          >
            Platform: {selectedRatio?.platform || "N/A"}
          </Typography>
          <Typography
            component="div"
            variant="body2"
            sx={{ color: "#9ca3af", fontSize: "0.75rem" }}
          >
            Resolution:{" "}
            {selectedRatio
              ? `${selectedRatio.width} × ${selectedRatio.height}px`
              : "N/A"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export { ASPECT_RATIOS };
